import {
    DragDropProvider,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
} from '@dnd-kit/react';
import {
    PointerActivationConstraints
} from '@dnd-kit/dom'
import type {
    Sortable
} from '@dnd-kit/dom/sortable'
import {
    closestCenter
} from '@dnd-kit/collision'
import {
    arrayMove,
} from '@dnd-kit/helpers';
import {IconEdit, IconGripVertical, IconPlus, IconTrash} from '@tabler/icons-react';
import cx from 'clsx';
import {Button, Group, Stack, Text} from '@mantine/core';
import {useListState, UseListStateHandlers} from '@mantine/hooks';
import classes from './ManageFavoriteLinksModal.module.css';
import TooltipActionIcon from "@/components/TooltipActionIcon.tsx";
import {useCallback, useMemo, useState} from "react";
import {modals} from "@mantine/modals";
import OmniFetchTS from "@/util/OmniFetchTS.tsx";
import {useSortable} from "@dnd-kit/react/sortable";
import {ILinkCategoryWithLinks} from "@/types/models/ILinkCategory.ts";
import {IGetFavoriteLinks} from "@/types/api/Link.ts";
import {IUserLink} from "@/types/models/IUserLink.ts";
import openAddEditFavoriteLinkModal from "@/components/SidebarLinks/AddEditFavoriteLinkModal/modal_utils.tsx";
import {MANAGE_FAVORITE_LINKS_MODAL_ID} from "@/components/SidebarLinks/ManageFavoriteLinksModal/modal_utils.tsx";
import {ILink} from "@/types/models/ILink.ts";

export type UserLinkEditable = Omit<IUserLink, "id" | "order" | "user_id" | "name" | "href"> & {id?: string | number, name: string | null, href: string | null}

interface ItemProps {
    item: UserLinkEditable
    items: UserLinkEditable[]
    handlers: UseListStateHandlers<UserLinkEditable>
    linkById: Record<string, ILink>
    editItem: (item: UserLinkEditable) => void
}

function SortableItem({ item, items, handlers, linkById, editItem }: ItemProps) {
    const { isDragging, ref, handleRef } = useSortable({
        index: items.indexOf(item),
        collisionDetector: closestCenter,
        id: `${item.id}`,
    })

    return (
        <div
            className={cx(classes.item, { [classes.itemDragging]: isDragging })} ref={ref}
        >
            <Group gap={"xs"}>
                <div className={classes.dragHandle} ref={handleRef}>
                    <IconGripVertical size={18} stroke={1.5} />
                </div>
                <Text>{item.link_id ? linkById[`${item.link_id}`].name : item.name ?? ""}</Text>
            </Group>
            <Group justify={"end"}>
                <TooltipActionIcon label={"Edit Favorite"} size={"lg"} onClick={() => editItem(item)}>
                    <IconEdit/>
                </TooltipActionIcon>
                <TooltipActionIcon label={"Remove Favorite"} size={"lg"} c={"red"} onClick={() => {
                    let index = 0
                    for (let iter of items) {
                        if (iter.id == item.id) {
                            break
                        }
                        index++
                    }
                    handlers.remove(index)
                }}>
                    <IconTrash/>
                </TooltipActionIcon>
            </Group>
        </div>
    );
}

export interface ManageFavoriteLinksModalProps {
    linkData: ILinkCategoryWithLinks[]
    reloadLinks: () => void
    favorites?: UserLinkEditable[]
}

export default function ManageFavoriteLinksModal({linkData, reloadLinks, favorites}: ManageFavoriteLinksModalProps) {
    const [stateRetrieved, setStateRetrieved] = useState<boolean>(!!favorites)
    const [state, handlers] = useListState<UserLinkEditable>(favorites ?? [])

    if (!stateRetrieved) {
        OmniFetchTS<IGetFavoriteLinks>("/api/v1/links/favorites").then((json) => {
            if (json.success && json.data) {
                handlers.setState(json.data.links.map((link) => ({
                    id: `${link.id}`,
                    link_id: link.link_id,
                    name: link.name,
                    href: link.href
                })))
                setStateRetrieved(true)
            }
        })
    }

    const linkById = useMemo(() => {
        let result: Record<string, ILink> = {}
        for (let category of linkData) {
            for (let link of category.links) {
                result[`${link.id}`] = link
            }
        }
        return result
    }, [linkData])

    const handleDragEnd = (event: DragEndEvent) => {
        const active: Sortable | null = event.operation.source as Sortable | null
        if (!active) return
        const oldIndex = active.initialIndex
        const newIndex = active.index
        handlers.setState(arrayMove(state, oldIndex, newIndex));
    };

    const apiState = useMemo(() => {
        let links: UserLinkEditable[] = []
        for (let link of state) {
            if (typeof link.id == "string") {
                let {id, ...linkWithoutId} = link
                links.push(linkWithoutId)
            } else {
                links.push(link)
            }
        }
        return links
    }, [state])

    const editFavorite = useCallback((userLink: UserLinkEditable) => {
        openAddEditFavoriteLinkModal(linkData, state, reloadLinks, userLink)
    }, [linkData, state, reloadLinks])

    return (
        <Stack gap={0}>
            <DragDropProvider sensors={() => [
                PointerSensor.configure({activationConstraints(event) {
                    if (event.pointerType === 'touch') {
                      return [
                        new PointerActivationConstraints.Delay({value: 500, tolerance: {x: 5, y: 5}}),
                      ];
                    }
                    return [new PointerActivationConstraints.Distance({value: 8})];
                }}),
                KeyboardSensor
            ]} onDragEnd={handleDragEnd}>
                {state.map((item) => (
                    <SortableItem key={`${item.id}`} item={item} items={state} handlers={handlers} linkById={linkById} editItem={editFavorite}/>
                ))}
            </DragDropProvider>
            <Stack>
                <Button size={"xs"} fullWidth={true} color={"green"} leftSection={<IconPlus/>} onClick={() => {
                    openAddEditFavoriteLinkModal(linkData, state, reloadLinks)
                }}>Add Favorite</Button>
                <Group justify={"end"}>
                    <Button variant="default" onClick={() => modals.close(MANAGE_FAVORITE_LINKS_MODAL_ID)}>Cancel</Button>
                    <Button color={"blue"} onClick={() => {
                        OmniFetchTS("/api/v1/links/favorites", {method: "POST"}, {links: apiState}).then((json) => {
                            if (json.success) {
                                reloadLinks()
                                modals.close(MANAGE_FAVORITE_LINKS_MODAL_ID)
                            }
                        })
                    }}>Save</Button>
                </Group>
            </Stack>
        </Stack>
    );
}